import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Tool } from '../types/database'
import { useScrapedTools } from './useScrapedTools'

export function useTools() {
  const [adminTools, setAdminTools] = useState<Tool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch scraped tools
  const { scrapedTools, loading: scrapedLoading } = useScrapedTools()

  useEffect(() => {
    fetchAdminTools()
  }, [])

  async function fetchAdminTools() {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('tools')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setAdminTools(data || [])
    } catch (err: any) {
      console.error('Error fetching admin tools:', err)
      setError(err.message)
      setAdminTools([])
    } finally {
      setLoading(false)
    }
  }

  // Merge admin tools and scraped tools
  // NO DUPLICATE FILTERING - show all tools so user can see and delete duplicates
  const mergeTools = (): Tool[] => {
    const merged: Tool[] = []

    // Add all admin tools first (with source marker)
    adminTools.forEach((tool) => {
      merged.push({
        ...tool,
        source: 'admin' as any,
      })
    })

    // Add ALL scraped tools (even if duplicates exist)
    scrapedTools.forEach((tool) => {
      merged.push({
        ...tool,
        source: 'scraped' as any,
      })
    })

    return merged
  }

  const tools = mergeTools()
  const isLoading = loading || scrapedLoading

  const getFeaturedTools = () => tools.filter((tool) => tool.is_featured)

  // Helper function to check if tool belongs to a category
  const toolBelongsToCategory = (tool: Tool, category: string): boolean => {
    if (category === 'all') return true

    // Check new categories array first (multi-category support)
    if (tool.categories && tool.categories.length > 0) {
      return tool.categories.includes(category)
    }

    // Fallback to legacy single category field
    return tool.category === category
  }

  const getToolsByCategory = (category: string) =>
    category === 'all'
      ? tools
      : tools.filter((tool) => toolBelongsToCategory(tool, category))

  const refresh = () => {
    fetchAdminTools()
  }

  return {
    tools,
    loading: isLoading,
    error,
    getFeaturedTools,
    getToolsByCategory,
    toolBelongsToCategory,
    refresh,
  }
}
